'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { client } from '@/sanity/lib/client';
import { CONTACT_SUPPORT_BY_PAGE_QUERY, CONTACT_SUPPORT_QUERY } from '@/sanity/queries/contactSupport';
import type { ContactSupportProps } from '@workspace/blocks/ContactSupport';
import { toVariant } from '@workspace/blocks/ContactSupport/lib';
import { urlFor } from '@/sanity/lib/image';
import { SanityImage } from '@workspace/interfaces';
import { namespaceType } from '@workspace/sanity-base/lib/namespace';

type UseContactSupportOptions = { isSkip?: boolean };

type UseContactSupportReturn = {
  enabled: boolean;
  data?: ContactSupportProps;
  loading: boolean;
  error?: string | null;
  refetch: () => Promise<void>;
};

const mapToProps = (raw: any): ContactSupportProps => {
  const data = raw?.contactSupport ?? {};

  const image: SanityImage | undefined = data?.imageLink?.image
    ? {
        asset: {
          _id: data.imageLink.image.asset?._id ?? '',
          _ref: data.imageLink.image.asset?._ref ?? '',
          _type: 'reference' as const,
          url: data.imageLink.image.asset?.url ?? '',
        },
        crop: data.imageLink.image.crop,
        hotspot: data.imageLink.image.hotspot,
        alt: data.imageLink.image.alt,
        url: data.imageLink.image.asset?.url,
      }
    : undefined;

  const imageLink = data?.imageLink?.link ?? {};
  const imageHref = imageLink?.href ?? imageLink?.url;
  const imageTarget = imageLink?.isOpenInNewTab ? ('_blank' as const) : '_self';

  const contactItems = Array.isArray(data?.contactInfo)
    ? data.contactInfo.map((datainfo: any) => {
        const link = datainfo?.textLink?.link ?? {};
        return {
          title: datainfo?.title,
          icon: datainfo?.iconName ?? datainfo?.icon?.icon ?? datainfo?.icon,
          text: datainfo?.textLink?.text ?? '',
          isBold: datainfo?.textLink?.isBold ?? true,
          href: link?.href ?? link?.url ?? undefined,
          target: link?.isOpenInNewTab ? ('_blank' as const) : '_self',
          iconImageUrl:
            datainfo?.iconImageUrl ??
            (datainfo?.icon?.iconImage?.asset ? urlFor(datainfo.icon.iconImage.asset).url() : undefined),
        };
      })
    : [];

  const button = data?.ctaButton && data.ctaButton.label
    ? [
        {
          children: data.ctaButton.label,
          variant: toVariant(data.ctaButton.style),
          href: data.ctaButton.link?.href ?? data.ctaButton.link?.url,
          target: data.ctaButton.link?.isOpenInNewTab ? '_blank' : '_self',
        },
      ]
    : [];

  return {
    heading: data?.heading?.heading ?? data?.heading ?? '',
    description: data?.description ?? '',
    image,
    imageAlt: image?.alt,
    imageHref,
    imageTarget,
    contactItems,
    button,
    newsletterHeading: data?.newsletterHeading,
    newsletterDescription: data?.newsletterDescription,
  } as ContactSupportProps;
};

const getCandidateIds = (docId: string): string[] => {
  if (!docId) return [];
  const isDraftId = docId.startsWith('drafts.');
  const baseId = isDraftId ? docId.replace(/^drafts\./, '') : docId;
  return isDraftId ? [docId, baseId] : [`drafts.${docId}`, docId];
};

const fetchByDocId = async (docId: string): Promise<any> => {
  const candidateIds = getCandidateIds(docId);

  for (const candidate of candidateIds) {
    if (!candidate || candidate === 'drafts.') continue;

    try {
      const result = await client.fetch(CONTACT_SUPPORT_QUERY, {
        docId: candidate,
      });

      if (result !== null && result !== undefined) {
        return result;
      }
    } catch {
      continue;
    }
  }

  return null;
};

const processFetchedData = (raw: any): { enabled: boolean; data?: ContactSupportProps } => {
  if (!raw || typeof raw !== 'object') {
    return { enabled: false, data: undefined };
  }

  const includeSection = !!raw.isIncludedContactSupport;
  const hasContent = !!raw.contactSupport;
  const ok = includeSection && hasContent;

  return {
    enabled: ok,
    data: ok ? mapToProps(raw) : undefined,
  };
};

export function useContactSupport(
  locator?: { docId?: string; pageType?: string; slug?: string } | string,
  { isSkip = false }: UseContactSupportOptions = {},
): UseContactSupportReturn {
  const [enabled, setEnabled] = useState(false);
  const [data, setData] = useState<ContactSupportProps | undefined>(undefined);
  const [loading, setLoading] = useState(!isSkip);
  const [error, setError] = useState<string | null>(null);

  // Extract individual values from locator to avoid object reference comparison issues
  const locatorDocId = typeof locator === 'string' ? locator : locator?.docId;
  const locatorPageType = typeof locator === 'object' && locator ? locator.pageType : undefined;
  const locatorSlug = typeof locator === 'object' && locator ? locator.slug : undefined;

  const { docId, pageType, slug, hasPageLocator, shouldFetch } = useMemo(() => {
    const docId = locatorDocId;
    const pageType = locatorPageType;
    const slug = locatorSlug;
    const hasPageLocator = !!(pageType && slug);
    const shouldFetch = !isSkip && (!!docId || hasPageLocator);

    return { docId, pageType, slug, hasPageLocator, shouldFetch };
  }, [locatorDocId, locatorPageType, locatorSlug, isSkip]);

  const fetchData = useCallback(async () => {
    if (isSkip || !shouldFetch) {
      setEnabled(false);
      setData(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let raw: any = null;

      if (docId) {
        raw = await fetchByDocId(docId);
      } else if (hasPageLocator && pageType && slug) {
        raw = await client.fetch(CONTACT_SUPPORT_BY_PAGE_QUERY, {
          pageType: namespaceType(pageType),
          slug,
        });
      }

      const { enabled: isEnabled, data: processedData } = processFetchedData(raw);
      setEnabled(isEnabled);
      setData(processedData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch contact support');
      setEnabled(false);
      setData(undefined);
    } finally {
      setLoading(false);
    }
  }, [docId, pageType, slug, isSkip, shouldFetch, hasPageLocator]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { enabled, data, loading, error, refetch: fetchData };
}
